import React, { useState, useEffect } from 'react';
import { 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Box, 
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon
} from '@mui/icons-material';
import { getAuthHeaders } from '../../utils/auth';

const PriceListCategoryManagement = () => {
  const [priceListCategories, setPriceListCategories] = useState([]);
  const [lessonCategories, setLessonCategories] = useState([]);
  const [passTypes, setPassTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    single_price: '',
    lesson_category_ids: [],
    pass_type_ids: [],
    in_price_list: true,
    sort_order: 0,
    additional_info: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const [categoriesRes, lessonCatsRes, passTypesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/price-list-categories/admin`, {
          headers
        }),
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-category/`, {
          headers
        }),
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/`, {
          headers
        })
      ]);

      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setPriceListCategories(data);
      } else {
        if (categoriesRes.status === 403 || categoriesRes.status === 401) {
          showSnackbar('Brak uprawnień do pobrania kategorii cennika', 'error');
        } else {
          showSnackbar('Failed to fetch price list categories', 'error');
        }
      }

      if (lessonCatsRes.ok) {
        const data = await lessonCatsRes.json();
        setLessonCategories(data.objects || []);
      } else {
        if (lessonCatsRes.status === 403 || lessonCatsRes.status === 401) {
          showSnackbar('Brak uprawnień do pobrania kategorii lekcji', 'error');
        } else {
          showSnackbar('Failed to fetch lesson categories', 'error');
        }
      }

      if (passTypesRes.ok) {
        const data = await passTypesRes.json();
        setPassTypes(data.objects || []);
      } else {
        if (passTypesRes.status === 403 || passTypesRes.status === 401) {
          showSnackbar('Brak uprawnień do pobrania typów karnetów', 'error');
        } else {
          showSnackbar('Failed to fetch pass types', 'error');
        }
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpen = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || '',
        single_price: category.single_price || '',
        lesson_category_ids: category.lesson_category_ids || [],
        pass_type_ids: category.pass_type_ids || [],
        in_price_list: category.in_price_list ?? true,
        sort_order: category.sort_order || 0,
        additional_info: category.additional_info || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        single_price: '',
        lesson_category_ids: [],
        pass_type_ids: [],
        in_price_list: true,
        sort_order: 0,
        additional_info: ''
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCategory(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const url = editingCategory 
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/price-list-categories/${editingCategory.id}`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/price-list-categories/`;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        method,
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          single_price: formData.single_price ? parseInt(formData.single_price) : null,
          sort_order: parseInt(formData.sort_order) || 0
        })
      });

      if (response.ok) {
        await fetchData();
        handleClose();
        showSnackbar(
          editingCategory ? 'Category updated successfully' : 'Category created successfully'
        );
      } else {
        if (response.status === 403 || response.status === 401) {
          showSnackbar('Brak uprawnień do zapisania kategorii', 'error');
        } else {
          const error = await response.text();
          showSnackbar(`Error: ${error}`, 'error');
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      showSnackbar('Error saving category', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/price-list-categories/${categoryId}`,
        {
          method: 'DELETE',
          headers
        }
      );

      if (response.ok) {
        await fetchData();
        showSnackbar('Category deleted successfully');
      } else {
        if (response.status === 403 || response.status === 401) {
          showSnackbar('Brak uprawnień do usunięcia kategorii', 'error');
        } else {
          showSnackbar('Error deleting category', 'error');
        }
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      showSnackbar('Error deleting category', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatArrayDisplay = (arr, items, nameField = 'name') => {
    if (!arr || arr.length === 0) return 'None';
    return arr.map(id => {
      const item = items.find(item => item.id === id);
      return item ? item[nameField] : `ID: ${id}`;
    }).join(', ');
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Price List Categories Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          disabled={loading}
        >
          Add New Category
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Single Price</TableCell>
              <TableCell>Lesson Categories</TableCell>
              <TableCell>Pass Types</TableCell>
              <TableCell>In Price List</TableCell>
              <TableCell>Sort Order</TableCell>
              <TableCell>Additional Info</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {priceListCategories.map((category) => (
              <TableRow key={category.id}>
                <TableCell>{category.id}</TableCell>
                <TableCell>{category.name}</TableCell>
                <TableCell>
                  {category.single_price ? `${category.single_price} zł` : 'N/A'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                    {formatArrayDisplay(category.lesson_category_ids, lessonCategories)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                    {formatArrayDisplay(category.pass_type_ids, passTypes)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={category.in_price_list ? 'Yes' : 'No'} 
                    color={category.in_price_list ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{category.sort_order}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 150, wordWrap: 'break-word' }}>
                    {category.additional_info || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpen(category)}
                    disabled={loading}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(category.id)}
                    disabled={loading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Price List Category' : 'Add New Price List Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <TextField
              label="Category Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              fullWidth
            />

            <TextField
              label="Single Price (in groszy)"
              type="number"
              value={formData.single_price}
              onChange={(e) => handleInputChange('single_price', e.target.value)}
              fullWidth
              helperText="Price in groszy (e.g., 5000 for 50 zł)"
            />

            <Autocomplete
              multiple
              options={lessonCategories}
              getOptionLabel={(option) => option.name}
              value={lessonCategories.filter(cat => formData.lesson_category_ids.includes(cat.id))}
              onChange={(_, newValue) => {
                handleInputChange('lesson_category_ids', newValue.map(item => item.id));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lesson Categories"
                  placeholder="Select lesson categories"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    variant="outlined"
                    label={option.name}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />

            <Autocomplete
              multiple
              options={passTypes}
              getOptionLabel={(option) => option.name}
              value={passTypes.filter(pass => formData.pass_type_ids.includes(pass.id))}
              onChange={(_, newValue) => {
                handleInputChange('pass_type_ids', newValue.map(item => item.id));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pass Types"
                  placeholder="Select pass types"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    variant="outlined"
                    label={option.name}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />

            <TextField
              label="Sort Order"
              type="number"
              value={formData.sort_order}
              onChange={(e) => handleInputChange('sort_order', e.target.value)}
              fullWidth
              helperText="Lower numbers appear first"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.in_price_list}
                  onChange={(e) => handleInputChange('in_price_list', e.target.checked)}
                />
              }
              label="Show in Price List"
            />

            <TextField
              label="Additional Info"
              value={formData.additional_info}
              onChange={(e) => handleInputChange('additional_info', e.target.value)}
              multiline
              rows={3}
              fullWidth
              helperText="Additional information to display (e.g., 'Zapisy przez telefon')"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || !formData.name.trim()}
          >
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PriceListCategoryManagement;
